/* Atelier Alpino (owner, 04/10): the story page keeps the live page's timeline and makes it the page's guide.
   /pages/la-nostra-storia is a custom-liquid section (templates/page.la-nostra-storia.json): under the hero, its
   timeline .sks-tl lists the years with their labels, in the page's own translated words. This module, loaded on that
   template only (layout/theme.liquid), changes none of the words:
   - each year becomes a button that takes you to the part of the story it names;
   - scrolled past, the timeline stays under the header as a slim bar of years (assets/luxe-pages.css folds the labels
     away with a clip-path, so its box and the page's layout never change) and rolls up when the chapters end;
   - its line fills up to the year being read, each dot turns green once its part of the story is reached, the year
     being read carries aria-current, and on phones the strip keeps that year in view;
   - the chapters' ski track (luxe-pages.css) draws down as the story is read.
   Without this script the timeline is the live one, still. Reduced motion: no smooth scrolling, the states still
   follow the reading. */

const READ = 0.3; // the reading line, this far down the screen under the header and the bar
const clamp = (value) => Math.min(1, Math.max(0, value));
const reduced = matchMedia('(prefers-reduced-motion: reduce)');

/* Where each year is told. A year whose four digits are in the text (the translations keep the numbers) is the first
   paragraph from the previous year's on that carries them; a year without (1940, "Oggi") takes the paragraph after the
   previous year's. The first year told in a chapter opens it, so it points at the chapter's heading. */
function places(items, chapters) {
  const paragraphs = chapters.flatMap((chapter) => [...chapter.querySelectorAll('.sks-ch__body p')].map((p) => ({ p, chapter })));
  if (!paragraphs.length) return null;
  const found = [];
  let from = 0;
  items.forEach((item, i) => {
    const year = (item.querySelector('.sks-tl__year')?.textContent.match(/\d{4}/) || [])[0];
    const index = year ? paragraphs.findIndex((entry, k) => k >= from && entry.p.textContent.includes(year)) : -1;
    found[i] = index;
    if (index >= 0) from = index;
  });
  let previous = -1;
  const indexes = found.map((index, i) => {
    if (index < 0) {
      const next = found.slice(i + 1).find((n) => n >= 0) ?? paragraphs.length;
      index = Math.max(0, Math.min(previous + 1, next - 1, paragraphs.length - 1), previous);
    }
    previous = index;
    return index;
  });
  const opened = new Set();
  return indexes.map((index) => {
    const { p, chapter } = paragraphs[index];
    if (opened.has(chapter)) return p;
    opened.add(chapter);
    return chapter.querySelector('.sks-ch__text') || chapter;
  });
}

function start(root) {
  const nav = root.querySelector('.sks-tl');
  const track = nav?.querySelector('.sks-tl__track');
  const items = nav ? [...nav.querySelectorAll('.sks-tl__item')] : [];
  const chapters = [...root.querySelectorAll('.sks-ch')];
  const story = root.querySelector('.sks-chapters');
  const anchors = items.length > 1 && chapters.length ? places(items, chapters) : null;
  if (!track || !story || !anchors || root.hasAttribute('data-sks-tl')) return null;
  const header = document.querySelector('.shopify-section--header');

  // each year a button: its dot, year and label move into it (the same nodes, so the same words and the same layout)
  const buttons = items.map((item, index) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'sks-tl__go';
    button.append(...item.childNodes);
    item.append(button);
    button.addEventListener('click', () => go(index));
    return button;
  });
  anchors.forEach((anchor) => anchor.setAttribute('data-sks-anchor', ''));
  // the timeline is now always at hand: its years come in once (the section's own reveal), all of them, so the
  // ones a phone's strip shows only after sliding are there too
  items.forEach((item) => item.classList.add('sks-vis'));
  root.setAttribute('data-sks-tl', '');

  let bar = 0;
  const measure = () => { bar = parseFloat(getComputedStyle(root).getPropertyValue('--sks-tl-bar')) || 0; };
  const cssHeader = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
  // where the header's lower edge sits once the page has scrolled (it is sticky): its offset plus its height
  const stuckHeader = () => {
    if (!header) return cssHeader();
    const style = getComputedStyle(header);
    return style.position === 'sticky' || style.position === 'fixed' ? (parseFloat(style.top) || 0) + header.offsetHeight : 0;
  };

  // a year: its part of the story lands on the reading line, so the year is the one being read; focus follows
  function go(index) {
    const anchor = anchors[index];
    const edge = stuckHeader() + bar;
    const line = edge + (innerHeight - edge) * READ;
    const top = scrollY + anchor.getBoundingClientRect().top - line + 2;
    scrollTo({ top: Math.max(0, Math.round(top)), behavior: reduced.matches ? 'auto' : 'smooth' });
    anchor.setAttribute('tabindex', '-1');
    anchor.focus({ preventScroll: true });
  }

  // on phones the strip keeps the year being read in view, unless the shopper has just moved it
  let touched = -Infinity;
  ['pointerdown', 'touchstart', 'wheel'].forEach((type) => track.addEventListener(type, () => { touched = performance.now(); }, { passive: true }));
  function center(index) {
    if (track.scrollWidth <= track.clientWidth + 1 || performance.now() - touched < 1500) return;
    const box = track.getBoundingClientRect();
    const item = items[index].getBoundingClientRect();
    const left = track.scrollLeft + item.left - box.left - (box.width - item.width) / 2;
    if (Math.abs(left - track.scrollLeft) > 1) track.scrollTo({ left: Math.max(0, left), behavior: reduced.matches ? 'auto' : 'smooth' });
  }

  const fills = items.map(() => -1);
  let drawn = -1;
  let edgeShown = -1;
  let current = -2;
  let frame = 0;
  function update() {
    frame = 0;
    // read everything first
    const edge = Math.max(0, header ? header.getBoundingClientRect().bottom : cssHeader());
    const navTop = nav.getBoundingClientRect().top;
    const tops = anchors.map((anchor) => anchor.getBoundingClientRect().top);
    const end = story.getBoundingClientRect().bottom;
    const stuck = navTop <= edge + 1;
    const line = edge + bar + (innerHeight - edge - bar) * READ;
    // then write
    if (Math.abs(edge - edgeShown) > 0.5) {
      edgeShown = edge;
      root.style.setProperty('--sks-tl-top', `${edge.toFixed(1)}px`);
    }
    let reading = -1;
    tops.forEach((top, i) => { if (top <= line) reading = i; });
    let progress = 0;
    items.forEach((item, i) => {
      const next = tops[i + 1];
      const fill = next === undefined ? 0 : Math.round(clamp((line - tops[i]) / Math.max(1, next - tops[i])) * 1000) / 1000;
      progress += fill;
      if (fill !== fills[i]) {
        fills[i] = fill;
        item.style.setProperty('--sks-fill', fill);
      }
      item.classList.toggle('is-reached', i <= reading);
    });
    nav.classList.toggle('is-stuck', stuck);
    nav.classList.toggle('is-done', stuck && end <= edge + bar);
    // the ski track draws in step with the timeline's line
    const draw = Math.round((progress / (items.length - 1)) * 1000) / 1000;
    if (draw !== drawn) {
      drawn = draw;
      story.style.setProperty('--sks-draw', draw);
    }
    if (reading !== current) {
      buttons.forEach((button, i) => (i === reading ? button.setAttribute('aria-current', 'step') : button.removeAttribute('aria-current')));
      current = reading;
      if (reading >= 0) center(reading);
    }
  }
  const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
  const resize = () => { measure(); schedule(); };
  const observer = new ResizeObserver(schedule); // photographs and fonts settle the chapters' heights
  observer.observe(root);
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', resize);
  addEventListener('pageshow', schedule);
  measure();
  update();
  return () => {
    observer.disconnect();
    removeEventListener('scroll', schedule);
    removeEventListener('resize', resize);
    removeEventListener('pageshow', schedule);
    cancelAnimationFrame(frame);
  };
}

let stop = null;
const boot = () => {
  const root = document.querySelector('.sks-storia');
  if (!root || root.hasAttribute('data-sks-tl')) return;
  stop?.();
  stop = start(root);
};
boot();
// the theme editor re-renders the section in place
document.addEventListener('shopify:section:load', boot);
